// shieldZones.js — shared geometry for the lecture video protection layer.
// Used by BOTH the student player (LecturePlayer) and the admin preview
// (ShieldSettings) so what the admin sees is exactly what the student gets.

// 34px white brand bar + ~48px YouTube control bar. Guard zones are never
// allowed to cover this bottom strip, so settings/quality/fullscreen and the
// whole control bar always stay clickable no matter the admin config.
export const CONTROL_STRIP_PX = 82;

export const clampPct = (v) => Math.min(100, Math.max(0, Number(v) || 0));

// Horizontal mirror of a rect (for RTL YouTube UI where buttons sit on the
// left instead of the right). Same size, opposite side.
export const mirrorX = (r) => ({ ...r, x: 100 - r.x - r.w });

// Turns the admin shield config into concrete guard rectangles + an optional
// guide divider. Modes:
//  - single (default): one rectangle {x,y,w,h}
//  - vsplit: vertical divider at split%; left zone height sizeA, right zone height sizeB
//  - hsplit: horizontal divider at split%; top zone width sizeA, bottom zone width sizeB
// When cfg.rtlMirror is on, everything is mirrored for Arabic (RTL) YouTube UI.
export const getGuardZones = (s = {}) => {
  let out;
  if (s.mode === 'vsplit') {
    const sp = clampPct(s.split ?? 50);
    out = {
      zones: [
        { x: 0, y: clampPct(s.yA ?? 0), w: sp, h: clampPct(s.sizeA ?? 100) },
        { x: sp, y: clampPct(s.yB ?? 0), w: 100 - sp, h: clampPct(s.sizeB ?? 50) },
      ],
      guide: { vertical: true, at: sp },
    };
  } else if (s.mode === 'hsplit') {
    const sp = clampPct(s.split ?? 50);
    out = {
      zones: [
        { x: clampPct(s.xA ?? 0), y: 0, w: clampPct(s.sizeA ?? 100), h: sp },
        { x: clampPct(s.xB ?? 0), y: sp, w: clampPct(s.sizeB ?? 50), h: 100 - sp },
      ],
      guide: { vertical: false, at: sp },
    };
  } else {
    out = {
      zones: [{ x: clampPct(s.x), y: clampPct(s.y), w: clampPct(s.w), h: clampPct(s.h) }],
      guide: null,
    };
  }
  if (s.rtlMirror) {
    out = {
      zones: out.zones.map(mirrorX),
      guide: out.guide ? { ...out.guide, at: out.guide.vertical ? 100 - out.guide.at : out.guide.at } : null,
    };
  }
  return out;
};

// Clips every zone so its bottom edge never enters the reserved control strip
// at the bottom of a box of `boxH` pixels. Zones fully inside the strip are dropped.
export const clampZonesToSafeStrip = (zones, boxH, stripPx = CONTROL_STRIP_PX) => {
  if (!boxH || boxH <= 0) return zones;
  const safeBottom = Math.min(100, ((boxH - stripPx) / boxH) * 100);
  const out = [];
  for (const z of zones || []) {
    const bottom = Math.min(z.y + z.h, safeBottom);
    const h = bottom - z.y;
    if (h > 0.5) out.push({ ...z, h });
  }
  return out;
};

// Height (% of a box of `boxH` px) reserved at the bottom for controls.
export const safeStripPct = (boxH, stripPx = CONTROL_STRIP_PX) => {
  if (!boxH || boxH <= 0) return 0;
  return Math.min(100, Math.max(0, (stripPx / boxH) * 100));
};

// Bottom edge (%) guards may reach inside a box of `boxH` px.
export const safeBottomPct = (boxH, stripPx = CONTROL_STRIP_PX) => {
  if (!boxH || boxH <= 0) return 100;
  return Math.min(100, ((boxH - stripPx) / boxH) * 100);
};

// YouTube's popup menus (settings / quality / subtitles) always open
// anchored above the control bar on the right side. This zone is ALWAYS left
// open — punched out of every overlay — so menus stay usable whatever the
// admin configures. Geometry is % of the frame box.
// Admin-configurable (menuX = left %, menuW = width %, menuH = height %);
// the top edge auto-anchors directly above the control strip.
export const MENU_DEFAULTS = { x: 58, w: 42, h: 55, y: null };
export const menuZoneRect = (stripPct, cfg = {}) => {
  const num = (v, fb) => {
    const n = Number(v);
    return Number.isNaN(n) ? fb : Math.min(100, Math.max(0, n));
  };
  const x = num(cfg.menuX, MENU_DEFAULTS.x);
  const w = Math.min(num(cfg.menuW, MENU_DEFAULTS.w), 100 - x);
  const h = num(cfg.menuH, MENU_DEFAULTS.h);
  // Manual Y when set; otherwise auto-anchor directly above the control strip.
  // Either way the hole never invades the control strip.
  const manualY = cfg.menuY !== null && cfg.menuY !== undefined && cfg.menuY !== '';
  const yRaw = manualY ? num(cfg.menuY, 0) : (100 - stripPct - h);
  const y = Math.max(0, yRaw);
  const hole = { x, y, w, h: Math.max(0, Math.min(y + h, 100 - stripPct) - y) };
  return cfg.rtlMirror ? mirrorX(hole) : hole;
};

// Subtracts `hole` from rect `r`. Returns 0-4 rects covering r minus the overlap.
export const subtractRect = (r, hole) => {
  const ix1 = Math.max(r.x, hole.x);
  const iy1 = Math.max(r.y, hole.y);
  const ix2 = Math.min(r.x + r.w, hole.x + hole.w);
  const iy2 = Math.min(r.y + r.h, hole.y + hole.h);
  if (ix1 >= ix2 || iy1 >= iy2) return [{ ...r }];
  const out = [];
  if (iy1 - r.y > 0.5) out.push({ x: r.x, y: r.y, w: r.w, h: iy1 - r.y });
  if (r.y + r.h - iy2 > 0.5) out.push({ x: r.x, y: iy2, w: r.w, h: r.y + r.h - iy2 });
  if (ix1 - r.x > 0.5) out.push({ x: r.x, y: iy1, w: ix1 - r.x, h: iy2 - iy1 });
  if (r.x + r.w - ix2 > 0.5) out.push({ x: ix2, y: iy1, w: r.x + r.w - ix2, h: iy2 - iy1 });
  return out;
};

// Punches the always-open menu hole out of a list of overlay rects.
export const punchMenuHole = (rects, stripPct, cfg = {}) => {
  const hole = menuZoneRect(stripPct, cfg);
  return (rects || []).flatMap((r) => subtractRect(r, hole));
};

// Inverts a rect into the strips AROUND it: top, bottom, left, right.
// Used for grace mode, where ONLY the given rect stays open/interactive and
// everything else is blocked. Strips never enter the reserved control strip.
// Returns { strips, hole } — hole is the open rect clamped to the safe area.
export const invertRectToStrips = (rect = {}, safeBottom = 100) => {
  const gx = clampPct(rect.x);
  const gy = clampPct(rect.y);
  const gw = Math.min(clampPct(rect.w ?? 0), 100 - gx);
  const holeBottom = Math.min(gy + clampPct(rect.h ?? 0), safeBottom);
  const gh = holeBottom - gy;
  const strips = [];
  if (gy > 0.5) strips.push({ x: 0, y: 0, w: 100, h: gy });
  const bottomH = safeBottom - holeBottom;
  if (bottomH > 0.5) strips.push({ x: 0, y: holeBottom, w: 100, h: bottomH });
  if (gx > 0.5 && gh > 0.5) strips.push({ x: 0, y: gy, w: gx, h: gh });
  const rightW = 100 - (gx + gw);
  if (rightW > 0.5 && gh > 0.5) strips.push({ x: gx + gw, y: gy, w: rightW, h: gh });
  return { strips, hole: { x: gx, y: gy, w: gw, h: Math.max(0, gh) } };
};
